// ./src/components/features/Travels/TravelList/TravelList.tsx
//
// List component for the Travels page.
// Renders travel records as responsive cards with Framer Motion
// entrance animations — no table view.
//
// TravelCard is a self-contained card that visually mirrors RecordCard's
// layout conventions (spacing, font sizes, hover actions, swipe behavior)
// but handles travel-specific data: date range, start → destination,
// duration, status badge, complete action.
//
// Follows the same structural patterns as LogbookList:
// - Context-vs-props resolution via useOptionalTravelsContext()
// - Swipeable actions on mobile, hover actions on desktop
// - Entrance animations via useEntranceAnimation
// - LoadMoreButton for pagination
// - RecordCardSkeleton for loading state

import React, { useMemo, useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Checkbox,
  Box,
  Badge,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import {
  getTravelTypeConfig,
  isTravelActive,
} from "@/config";

import { formatNumber } from "@/utils/formatHelpers";
import { formatDateTime } from "@/utils/dateHelpers";
import { Travel } from "@/utils/api/TravelService";

import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useDashboardContext } from "@/contexts/DashboardContext";
import { useOptionalTravelsContext } from "@/contexts/TravelsContext";

import { AppIcon } from "@/components/generic/AppIcon";
import { EmptyState } from "@/components/generic/EmptyState";
import {
  useSwipeableList,
  SwipeableCard,
  SwipeAction,
} from "@/components/generic/SwipeableCard";

import { RecordCardSkeleton } from "@/components/generic/RecordCard";
import { LoadMoreButton } from "@/components/generic/LoadMoreButton";
import { TagBadgeGroup } from "@/components/generic/TagBadge";
import { OdometerDisplay } from "@/components/generic/OdometerDisplay";

import { TravelStatusBadge } from "../TravelStatusBadge";
import { AuditableRecord, AuditInfo } from "@/components/generic/AuditInfo";

// ============================================
// Styles — matches RecordCard hover pattern
// ============================================

const ACTIONS_CLASS = "travel-card__actions";
const CARD_CLASS = "travel-card";

const CARD_STYLES = `
  .${ACTIONS_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }

  @media (hover: hover) {
    .${CARD_CLASS}:hover .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .${CARD_CLASS}:focus-within .${ACTIONS_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-travel-card", "");
  style.textContent = CARD_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
};

// ============================================
// Helpers
// ============================================

const formatDuration = (
  minutes: number | undefined | null,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  if (minutes == null) return "";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return t("travels:list.durationMinutes", { mins, defaultValue: "{{mins}}m" });
  if (mins === 0) return t("travels:list.durationHours", { hours, defaultValue: "{{hours}}h" });
  return t("travels:list.durationFull", { hours, mins, defaultValue: "{{hours}}h {{mins}}m" });
};

/**
 * Build a location display string from a travel point's whereDone and location fields.
 * Returns whereDone · location if both exist, otherwise whichever is available.
 */
const buildPointLocationLabel = (
  whereDone: string | undefined | null,
  location: string | undefined | null
): string | null => {
  const hasWhere = Boolean(whereDone);
  const hasLocation = Boolean(location);
  if (hasWhere && hasLocation) return `${whereDone} · ${location}`;
  if (hasWhere) return whereDone!;
  if (hasLocation) return location!;
  return null;
};

// ============================================
// Types
// ============================================

export interface TravelListProps {
  /** Travel records (falls back to context if not provided) */
  records?: Travel[];

  /** Loading state (falls back to context) */
  loading?: boolean;

  /** Loading more state (falls back to context) */
  loadingMore?: boolean;

  /** Whether more items exist (falls back to context) */
  hasMore?: boolean;

  /** Load more callback (falls back to context) */
  onLoadMore?: () => void;

  /** Selected travel IDs */
  selectedIds?: string[];

  /** Selection change callback */
  onSelectionChange?: (selectedIds: string[]) => void;

  /** Edit callback */
  onEdit?: (travel: Travel) => void;

  /** Delete callback */
  onDelete?: (travel: Travel) => void;

  /** Complete callback (for in-progress travels) */
  onComplete?: (travel: Travel) => void;

  /** View waypoints callback */
  onViewWaypoints?: (travel: Travel) => void;

  /** Record click callback */
  onRecordClick?: (travel: Travel) => void;

  /** ID of travel being deleted (falls back to context) */
  deletingId?: string | null;

  /** ID of highlighted travel */
  highlightedId?: string | null;

  /** Add button callback (for empty state) */
  onAdd?: () => void;

  /** Enable selection checkboxes */
  selectable?: boolean;

  /** Status filter — which statuses to show */
  activeStatuses?: number[];
}

// ============================================
// Loading Skeleton
// ============================================

const LoadingSkeleton: React.FC = () => {
  return (
    <Stack gap="sm">
      {Array.from({ length: 5 }).map((_, index) => (
        <RecordCardSkeleton
          key={index}
          showPrice={false}
          customContentRows={index % 2 === 0 ? 2 : 1}
        />
      ))}
    </Stack>
  );
};

// ============================================
// TravelCard — self-contained card matching
// RecordCard visual conventions
// ============================================

interface TravelCardProps {
  record: Travel;
  selected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onClick?: (record: Travel) => void;
  onEdit?: (record: Travel) => void;
  onDelete?: (record: Travel) => void;
  onComplete?: (record: Travel) => void;
  onViewWaypoints?: (record: Travel) => void;
  selectable: boolean;
  highlighted: boolean;
  isDeleting: boolean;
  distanceLabel: string;
  odometerUnit: string;
  // Swipe management
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const TravelCardComponent: React.FC<TravelCardProps> = ({
  record,
  selected,
  onSelect,
  onClick,
  onEdit,
  onDelete,
  onComplete,
  onViewWaypoints,
  selectable,
  highlighted,
  isDeleting,
  distanceLabel,
  odometerUnit,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["travels", "common"]);

  injectStyles();

  const isActive = isTravelActive(record.status);
  const travelTypeConfig = getTravelTypeConfig(record.travelType);
  const hasDistance = record.distance != null && record.distance > 0;
  const hasDuration = record.totalMinutes != null && record.totalMinutes > 0;
  const hasPurpose = Boolean(record.purpose);
  const hasDestination = Boolean(record.destination);
  const hasOdometer = record.firstOdometer != null;
  const hasTags = record.tags && record.tags.length > 0;
  const hasActions = Boolean(onEdit) || Boolean(onDelete) || Boolean(isActive && onComplete) || Boolean(onViewWaypoints);

  // Start location from firstRecord
  const startLocationLabel = useMemo(
    () => buildPointLocationLabel(record.firstRecord?.whereDone, record.firstRecord?.location),
    [record.firstRecord?.whereDone, record.firstRecord?.location]
  );

  // End location from lastRecord
  const endLocationLabel = useMemo(
    () => buildPointLocationLabel(record.lastRecord?.whereDone, record.lastRecord?.location),
    [record.lastRecord?.whereDone, record.lastRecord?.location]
  );

  // Reimbursement display
  const hasReimbursement =
    record.calculatedReimbursement != null &&
    record.calculatedReimbursement > 0 &&
    record.reimbursementRateCurrency;

  // ── Event handlers ──

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (record.id) {
        onSelect(record.id, event.currentTarget.checked);
      }
    },
    [record.id, onSelect]
  );

  const handleClick = useCallback(() => {
    onClick?.(record);
  }, [onClick, record]);

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit?.(record);
    },
    [onEdit, record]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(record);
    },
    [onDelete, record]
  );

  const handleCompleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onComplete?.(record);
    },
    [onComplete, record]
  );

  const handleViewWaypointsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onViewWaypoints?.(record);
    },
    [onViewWaypoints, record]
  );

  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => {
      onSwipeStateChange?.(isOpen);
    },
    [onSwipeStateChange]
  );

  // ── Swipe actions (mobile) ──

  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (isActive && onComplete) {
      actions.push({
        key: "complete",
        icon: "CircleCheck",
        label: t("travels:actions.complete", { defaultValue: "Complete" }),
        color: "green",
        onClick: () => onComplete(record),
        disabled: isDeleting,
      });
    }

    if (onViewWaypoints) {
      actions.push({
        key: "waypoints",
        icon: "Route",
        label: t("travels:actions.viewWaypoints", { defaultValue: "Waypoints" }),
        color: "violet",
        onClick: () => onViewWaypoints(record),
        disabled: isDeleting,
      });
    }

    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(record),
        disabled: isDeleting,
      });
    }

    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(record),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [isActive, onComplete, onEdit, onDelete, record, isDeleting, t]);

  // ── Status badge ──

  const statusBadge = <TravelStatusBadge status={record.status} size="sm" />;

  // ── Date range display ──

  const dateRangeDisplay = useMemo(() => {
    if (!record.firstDttm) return null;

    const startDate = formatDateTime(record.firstDttm, "datetime");

    if (record.lastDttm) {
      // Check if same day — show only time for end if same day
      const startDay = record.firstDttm.substring(0, 10);
      const endDay = record.lastDttm.substring(0, 10);

      if (startDay === endDay) {
        const endTime = formatDateTime(record.lastDttm, "time");
        return `${startDate} → ${endTime}`;
      }

      const endDate = formatDateTime(record.lastDttm, "datetime");
      return `${startDate} → ${endDate}`;
    }

    return startDate;
  }, [record.firstDttm, record.lastDttm]);

  // ── Desktop hover actions ──

  const hoverActionsEl = hasActions && (
    <Group gap={4} wrap="nowrap" className={ACTIONS_CLASS}>
      {isActive && onComplete && (
        <Tooltip label={t("travels:actions.complete", { defaultValue: "Complete" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="green"
            size="md"
            onClick={handleCompleteClick}
            disabled={isDeleting}
            aria-label={t("travels:actions.complete", { defaultValue: "Complete" })}
          >
            <AppIcon iconName="CircleCheck" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onViewWaypoints && (
        <Tooltip label={t("travels:actions.viewWaypoints", { defaultValue: "Waypoints" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="violet"
            size="md"
            onClick={handleViewWaypointsClick}
            disabled={isDeleting}
            aria-label={t("travels:actions.viewWaypoints", { defaultValue: "Waypoints" })}
          >
            <AppIcon iconName="Route" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={handleEditClick}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={handleDeleteClick}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  // ── Travel content rows (shared between mobile/desktop) ──

  const travelContentRows = (
    <>
      {/* Row: Distance · Duration · Round trip · Travel type */}
      <Group gap="sm" wrap="wrap" align="center">
        {hasDistance && (
          <Group gap={4}>
            <AppIcon iconName="Route" size={16} color="gray" />
            <Text size="md" fw={500}>
              {formatNumber(record.distance, 1)} {distanceLabel}
            </Text>
          </Group>
        )}

        {hasDuration && (
          <Group gap={4}>
            <AppIcon iconName="Clock" size={16} color="gray" />
            <Text size="md" c="dimmed">
              {formatDuration(record.totalMinutes, t)}
            </Text>
          </Group>
        )}

        {record.isRoundTrip && (
          <Badge variant="light" size="sm" color="gray">
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName="Repeat" size={12} />
              {t("travels:labels.roundTrip", { defaultValue: "Round Trip" })}
            </Group>
          </Badge>
        )}

        {travelTypeConfig && (
          <Badge variant="outline" size="sm" color={travelTypeConfig.color}>
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName={travelTypeConfig.icon} size={12} />
              {t(travelTypeConfig.translationKey)}
            </Group>
          </Badge>
        )}

        {hasReimbursement && (
          <Group gap={4}>
            <AppIcon iconName="Cash" size={16} color="gray" />
            <Text size="md" c="dimmed">
              {formatNumber(record.calculatedReimbursement, 2)} {record.reimbursementRateCurrency}
            </Text>
          </Group>
        )}
      </Group>

      {/* Row: Purpose */}
      {hasPurpose && (
        <Text size="md" lineClamp={1}>
          {record.purpose}
        </Text>
      )}

      {/* Row: Start → Destination locations */}
      {(startLocationLabel || hasDestination) && (
        <Group gap={6} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          <AppIcon
            iconName="MapPin"
            size={16}
            color="gray"
            style={{ flexShrink: 0 }}
          />
          <Text size="md" c="dimmed" lineClamp={1} style={{ minWidth: 0 }}>
            {startLocationLabel && hasDestination
              ? `${startLocationLabel} → ${record.destination}`
              : startLocationLabel || record.destination}
          </Text>
        </Group>
      )}

      {/* Row: End location (if different from destination label and lastRecord exists) */}
      {endLocationLabel && !hasDestination && (
        <Group gap={6} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          <AppIcon
            iconName="MapPinFilled"
            size={16}
            color="gray"
            style={{ flexShrink: 0 }}
          />
          <Text size="md" c="dimmed" lineClamp={1} style={{ minWidth: 0 }}>
            {endLocationLabel}
          </Text>
        </Group>
      )}
    </>
  );

  // ── Tags ──

  const tagsSection = hasTags && (
    <TagBadgeGroup
      tags={record.tags}
      size="sm"
      variant="filled"
      maxVisible={3}
      gap={8}
    />
  );

  // ============================================
  // MOBILE LAYOUT (<sm)
  // ============================================

  const mobileLayout = (
    <Box hiddenFrom="sm">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {/* Checkbox */}
        {selectable && (
          <Box pt={3}>
            <Checkbox
              checked={selected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              disabled={isDeleting}
              size="sm"
              aria-label={t("common:form.selectItem", { defaultValue: "Select item" })}
            />
          </Box>
        )}

        {/* Content */}
        <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
          {/* Header: Status badge + Date range */}
          <Group gap="xs" wrap="nowrap" align="center">
            {statusBadge}
            {dateRangeDisplay && (
              <Text size="md" fw={600} truncate="end" style={{ minWidth: 0 }}>
                {dateRangeDisplay}
              </Text>
            )}
          </Group>

          {/* Car · Odometer */}
          {(record.car?.label || hasOdometer) && (
            <Group gap="md" wrap="wrap">
              {record.car?.label && (
                <Group gap={6}>
                  <AppIcon iconName="Car" size={16} color="gray" />
                  <Text size="md" c="dimmed">
                    {record.car.label}
                  </Text>
                </Group>
              )}
              {hasOdometer && (
                <Group gap={6}>
                  <AppIcon iconName="Gauge" size={16} color="gray" />
                  <OdometerDisplay
                    value={record.firstOdometer}
                    distanceLabel={odometerUnit}
                    size="md"
                  />
                </Group>
              )}
            </Group>
          )}

          {/* Travel-specific content */}
          {travelContentRows}

          {/* Tags */}
          {tagsSection}

          {/* Audit info */}
          <AuditInfo record={record as AuditableRecord} />
        </Stack>
      </Group>
    </Box>
  );

  // ============================================
  // DESKTOP LAYOUT (sm+)
  // ============================================

  const desktopLayout = (
    <Box visibleFrom="sm">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {/* Checkbox */}
        {selectable && (
          <Box pt={4}>
            <Checkbox
              checked={selected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              disabled={isDeleting}
              size="md"
              aria-label={t("common:form.selectItem", { defaultValue: "Select item" })}
            />
          </Box>
        )}

        {/* Content */}
        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          {/* Header row: Status + Date range · Car · Odometer ... [Actions] */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="md" wrap="wrap" align="center" style={{ flex: 1 }}>
              {statusBadge}

              {dateRangeDisplay && (
                <Text size="md" fw={600}>
                  {dateRangeDisplay}
                </Text>
              )}

              {record.car?.label && (
                <Group gap={6}>
                  <AppIcon iconName="Car" size={16} color="gray" />
                  <Text size="md" c="dimmed">
                    {record.car.label}
                  </Text>
                </Group>
              )}

              {hasOdometer && (
                <Group gap={6}>
                  <AppIcon iconName="Gauge" size={16} color="gray" />
                  <OdometerDisplay
                    value={record.firstOdometer}
                    distanceLabel={odometerUnit}
                    size="md"
                  />
                </Group>
              )}
            </Group>

            {/* Right side: hover actions */}
            <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
              {hoverActionsEl}
            </Group>
          </Group>

          {/* Travel-specific content rows */}
          {travelContentRows}

          {/* Footer: tags */}
          {tagsSection}

          {/* Audit info */}
          <AuditInfo record={record as AuditableRecord} />
        </Stack>
      </Group>
    </Box>
  );

  // ============================================
  // Render — swipeable on mobile, plain on desktop
  // ============================================

  const cardPaperProps = {
    p: "sm" as const,
    withBorder: true,
    bg: highlighted ? ("green.0" as const) : undefined,
    opacity: isDeleting ? 0.5 : 1,
    className: CARD_CLASS,
    style: { transition: "box-shadow 150ms ease, opacity 150ms ease" },
    onClick: handleClick,
    tabIndex: onClick ? 0 : undefined,
    role: onClick ? ("button" as const) : undefined,
  };

  if (leftSwipeActions.length > 0) {
    return (
      <>
        {/* Mobile: swipeable */}
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={leftSwipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={handleSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-default)"
          >
            <Paper {...cardPaperProps}>
              {mobileLayout}
            </Paper>
          </SwipeableCard>
        </Box>

        {/* Desktop: plain card with hover actions */}
        <Box visibleFrom="sm">
          <Paper {...cardPaperProps}>
            {desktopLayout}
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <Paper {...cardPaperProps}>
      {mobileLayout}
      {desktopLayout}
    </Paper>
  );
};

const TravelCard = React.memo(TravelCardComponent);

// ============================================
// Main Component
// ============================================

const TravelListComponent: React.FC<TravelListProps> = ({
  records: recordsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  selectedIds = [],
  onSelectionChange,
  onEdit,
  onDelete,
  onComplete,
  onViewWaypoints,
  onRecordClick,
  deletingId: deletingIdProp,
  highlightedId,
  onAdd,
  selectable = true,
  activeStatuses,
}) => {
  const { t } = useTranslation(["travels", "common"]);
  const { distanceLabelShort } = useUserProfileContext();
  const { getCarById } = useDashboardContext();

  // ── Context vs Props resolution ──
  const travelsContext = useOptionalTravelsContext();

  const records = recordsProp ?? travelsContext?.travels ?? [];
  const loading = loadingProp ?? travelsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? travelsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? travelsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? travelsContext?.loadMore;
  const deletingId = deletingIdProp ?? travelsContext?.removingId ?? null;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation
  const { hasAnimated } = useEntranceAnimation({ itemCount: records.length });

  // ── Filtered records ──

  const filteredRecords = useMemo(() => {
    if (!activeStatuses || activeStatuses.length === 0) return records;
    return records.filter((r) => r.status != null && activeStatuses.includes(r.status));
  }, [records, activeStatuses]);

  // ── Selection ──

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleSelect = useCallback(
    (id: string, checked: boolean) => {
      if (!onSelectionChange) return;
      const newSelection = checked
        ? [...selectedIds, id]
        : selectedIds.filter((sid) => sid !== id);
      onSelectionChange(newSelection);
    },
    [selectedIds, onSelectionChange]
  );

  // ── Card interaction handlers ──

  const handleRecordClick = useCallback(
    (record: Travel) => {
      closeAllCards();
      onRecordClick?.(record);
    },
    [onRecordClick, closeAllCards]
  );

  const handleEdit = useCallback(
    (record: Travel) => {
      closeAllCards();
      onEdit?.(record);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (record: Travel) => {
      closeAllCards();
      onDelete?.(record);
    },
    [onDelete, closeAllCards]
  );

  const handleComplete = useCallback(
    (record: Travel) => {
      closeAllCards();
      onComplete?.(record);
    },
    [onComplete, closeAllCards]
  );

  const handleViewWaypoints = useCallback(
    (record: Travel) => {
      closeAllCards();
      onViewWaypoints?.(record);
    },
    [onViewWaypoints, closeAllCards]
  );

  // ── Swipe state ──

  const handleSwipeStateChange = useCallback(
    (recordId: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(recordId);
      }
    },
    [openCard]
  );

  // ── Odometer unit per record ──

  const getOdometerUnit = useCallback(
    (record: Travel): string => {
      const car = getCarById(record.carId);
      return car?.mileageIn || distanceLabelShort;
    },
    [getCarById, distanceLabelShort]
  );

  // ── Loading state ──

  if (loading && records.length === 0) {
    return <LoadingSkeleton />;
  }

  // ── Empty state ──

  if (!loading && records.length === 0) {
    return (
      <Paper withBorder p="xl">
        <EmptyState
          title={t("travels:emptyState.title")}
          description={t("travels:emptyState.description")}
          iconName="Route"
          actionLabel={t("travels:emptyState.actionLabel")}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  // ── Filtered empty ──

  const filtersHideAll = filteredRecords.length === 0 && records.length > 0;

  return (
    <Stack gap="sm">
      {/* Filtered-empty state */}
      {filtersHideAll && (
        <Paper withBorder p="xl">
          <EmptyState
            title={t("travels:emptyState.filteredTitle", { defaultValue: "No matching travels" })}
            description={t("travels:emptyState.filteredDescription", { defaultValue: "Try adjusting your filters to see more results." })}
            iconName="Filter"
            size="sm"
          />
        </Paper>
      )}

      {/* Card list with animations */}
      {!filtersHideAll && (
        <>
          <motion.div
            variants={hasAnimated ? undefined : entranceListVariants}
            initial={hasAnimated ? false : "hidden"}
            animate={hasAnimated ? undefined : "visible"}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--mantine-spacing-sm)",
            }}
          >
            <AnimatePresence mode="sync">
              {filteredRecords.map((record) => (
                <motion.div
                  key={record.id}
                  variants={hasAnimated ? undefined : entranceCardVariants}
                  initial={hasAnimated ? false : "hidden"}
                  animate={hasAnimated ? undefined : "visible"}
                  exit="exit"
                  layout
                >
                  <TravelCard
                    record={record}
                    selected={selectedSet.has(record.id || "")}
                    onSelect={handleSelect}
                    onClick={handleRecordClick}
                    onEdit={onEdit ? handleEdit : undefined}
                    onDelete={onDelete ? handleDelete : undefined}
                    onComplete={onComplete ? handleComplete : undefined}
                    onViewWaypoints={onViewWaypoints ? handleViewWaypoints : undefined}
                    selectable={selectable}
                    highlighted={record.id === highlightedId}
                    isDeleting={deletingId === record.id}
                    distanceLabel={t(`common:${distanceLabelShort}`)}
                    odometerUnit={t(`common:${getOdometerUnit(record)}`)}
                    forceClose={shouldForceClose(record.id || "")}
                    onForceCloseComplete={() =>
                      handleForceCloseComplete(record.id || "")
                    }
                    onSwipeStateChange={(isOpen) =>
                      handleSwipeStateChange(record.id || "", isOpen)
                    }
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          <LoadMoreButton
            hasMore={hasMore}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={onLoadMore}
          />
        </>
      )}
    </Stack>
  );
};

export const TravelList = React.memo(TravelListComponent);